
import { supabase } from "@/integrations/supabase/client";

/**
 * Detects which modern image formats the browser supports
 * @returns Object with support flags for avif and webp
 */
export async function detectImageFormatSupport(): Promise<{ avif: boolean, webp: boolean }> {
  return new Promise((resolve) => {
    const avifSupport = new Image();
    avifSupport.onload = () => {
      // AVIF supported
      resolve({
        avif: true,
        webp: true // Assume WebP is supported if AVIF is
      });
    };
    
    avifSupport.onerror = () => {
      // AVIF not supported, check WebP
      const webpSupport = new Image();
      webpSupport.onload = () => {
        resolve({
          avif: false,
          webp: true
        });
      };
      
      webpSupport.onerror = () => {
        resolve({
          avif: false,
          webp: false
        });
      };
      
      webpSupport.src = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
    };
    
    // Start testing AVIF
    avifSupport.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUEAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABoAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=';
  });
}

/**
 * Selects the best image variant based on device and network constraints
 * @param variants Object containing different image variants
 * @param targetWidth Target display width
 * @param isMobile Whether the device is mobile
 * @param isLowData Whether the user is on a low-data connection
 * @returns URL of the selected variant
 */
export function selectImageVariant(
  variants: Record<string, string>,
  targetWidth: number = 800,
  isMobile: boolean = false,
  isLowData: boolean = false
): string {
  // If we're on low data mode, prioritize smallest images
  if (isLowData) {
    if (variants.preview) return variants.preview;
    if (variants.thumbnail) return variants.thumbnail;
    
    // Find the smallest image by looking for width indicators in keys
    const smallestVariant = Object.entries(variants)
      .filter(([key]) => key.includes('_w'))
      .sort(([keyA], [keyB]) => {
        const widthA = parseInt(keyA.match(/_w(\d+)_/)?.[1] || '9999');
        const widthB = parseInt(keyB.match(/_w(\d+)_/)?.[1] || '9999');
        return widthA - widthB;
      })[0];
      
    if (smallestVariant) return smallestVariant[1];
  }
  
  // On mobile, prioritize mobile-optimized variants
  if (isMobile) {
    if (variants.avif_mobile) return variants.avif_mobile;
    if (variants.webp_mobile) return variants.webp_mobile;
    if (variants.preview) return variants.preview;
  }
  
  // Choose the best quality variant closest to target width
  // Priority: avif > webp > jpg/png
  if (targetWidth <= 400) {
    if (variants.avif_mobile) return variants.avif_mobile;
    if (variants.webp_mobile) return variants.webp_mobile;
    if (variants.preview) return variants.preview;
  }
  
  // Default to best quality
  if (variants.avif) return variants.avif;
  if (variants.webp) return variants.webp;
  if (variants.main) return variants.main;
  
  // Fallback to first available variant
  const firstKey = Object.keys(variants)[0];
  return firstKey ? variants[firstKey] : '';
}

/**
 * Creates or retrieves a placeholder image
 * @param imageUrl The full URL of the original image
 * @param originalPath The storage path of the original image
 * @returns Promise resolving to the placeholder URL or null
 */
export async function createPlaceholderImage(
  imageUrl: string,
  originalPath: string
): Promise<string | null> {
  try {
    // Extract the filename
    const pathParts = originalPath.split('/');
    const filename = pathParts[pathParts.length - 1];
    
    // Check if a placeholder already exists
    const placeholderPath = `placeholders/${filename}`;
    const { data: existingData } = await supabase.storage
      .from('placeholders')
      .list('', {
        search: filename
      });
      
    if (existingData && existingData.length > 0) {
      // Placeholder already exists, return its URL
      const { data } = supabase.storage
        .from('placeholders')
        .getPublicUrl(filename);
        
      return data.publicUrl;
    }
    
    // Placeholder doesn't exist, generate a tiny version using the transformation API
    // This is a simplified placeholder - a real implementation might generate a proper blurred placeholder
    const placeholderUrl = `https://ginefbvgxcobyuocxerb.supabase.co/storage/v1/render/image/public/services/${originalPath}?width=20&quality=30&blur=10`;
    
    // In a real implementation, you might download this placeholder, then upload it to the placeholders bucket
    // For now, we'll just return the transformation URL directly
    return placeholderUrl;
  } catch (error) {
    console.error('Error creating placeholder image:', error);
    return null;
  }
}

/**
 * Gets the URL for a placeholder image based on the original path
 * @param originalPath The storage path of the original image
 * @returns The placeholder URL
 */
export function getPlaceholderUrl(originalPath: string): string {
  // Extract the filename
  const pathParts = originalPath.split('/');
  const filename = pathParts[pathParts.length - 1];
  
  // Get the placeholder URL using the transformation API
  return `https://ginefbvgxcobyuocxerb.supabase.co/storage/v1/render/image/public/services/${originalPath}?width=20&quality=30&blur=10`;
}
